import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useMe } from '@/api/auth';
import type { Guid, MeDto, OrgRole } from '@/api/types';
import { STORAGE_KEYS, storage } from '@/lib/storage';

export type Area = 'customer' | 'lead' | 'tester' | 'admin';

export function areasOf(me: MeDto | undefined): Area[] {
  if (!me) return [];
  const areas: Area[] = [];
  if (me.organizations.length > 0) areas.push('customer');
  if (me.communities.some((c) => c.role === 'Lead' && c.status === 'Active')) areas.push('lead');
  if (me.hasTesterProfile || me.communities.some((c) => c.status === 'Active')) areas.push('tester');
  if (me.isPlatformAdmin) areas.push('admin');
  return areas;
}

export const AREA_HOME: Record<Area, string> = {
  customer: '/projects',
  lead: '/communities',
  tester: '/my/assignments',
  admin: '/admin',
};

/** Which area a path belongs to (for highlighting the switcher). */
export function areaOfPath(pathname: string): Area | null {
  if (/^\/admin(\/|$)/.test(pathname)) return 'admin';
  if (/^\/executions\/[^/]+\/run/.test(pathname)) return 'tester';
  if (/^\/(my|run|jobs|teams)(\/|$)/.test(pathname)) return 'tester';
  if (/^\/talent(\/|$)/.test(pathname)) return 'customer';
  if (/^\/(communities|team-panel)(\/|$)/.test(pathname)) return 'lead';
  if (/^\/(projects|org|cycles|executions|bugs)(\/|$)/.test(pathname)) return 'customer';
  return null;
}

// ----------------------------------------------------------------------------- current organization
const orgListeners = new Set<() => void>();
let storedOrgId: string | null = storage.get(STORAGE_KEYS.orgId);

export function setCurrentOrgId(id: Guid) {
  if (storedOrgId === id) return;
  storedOrgId = id;
  storage.set(STORAGE_KEYS.orgId, id);
  orgListeners.forEach((l) => l());
}

export function useCurrentOrg() {
  const { data: me } = useMe();
  const selected = useSyncExternalStore(
    (cb) => {
      orgListeners.add(cb);
      return () => orgListeners.delete(cb);
    },
    () => storedOrgId,
  );
  const org = useMemo(() => {
    const orgs = me?.organizations ?? [];
    return orgs.find((o) => o.id === selected) ?? orgs[0] ?? null;
  }, [me, selected]);
  const select = useCallback((id: Guid) => setCurrentOrgId(id), []);
  /** False until /api/me has loaded: `org === null` only means "no organization" once this is true. */
  const ready = !!me;
  return { org, organizations: me?.organizations ?? [], select, ready };
}

export function useOrgRole(orgId: Guid | null | undefined): OrgRole | null {
  const { data: me } = useMe();
  if (!orgId || !me) return null;
  return me.organizations.find((o) => o.id === orgId)?.role ?? null;
}

export function canManage(role: OrgRole | null): boolean {
  return role === 'Owner' || role === 'Admin';
}

export function canReview(role: OrgRole | null): boolean {
  return role === 'Owner' || role === 'Admin' || role === 'Reviewer';
}
